import { useRef } from "react";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Point,
} from "chart.js";
import { Line } from "react-chartjs-2";
import type { ChartOptions } from "chart.js";
import { useChartData } from "@/hooks/useChartData";

type LineChartProps = {
  url: string;
  className: string;
  options?: ChartOptions<"line">;
  min?: number;
  max?: number;
  name: string;
};

Chart.register(CategoryScale, LinearScale, PointElement, LineElement);

export const LineChart = ({
  url,
  options,
  min,
  max,
  name,
  className,
}: LineChartProps) => {
  const chartRef = useRef(null);

  const { showChart, data, baseOptions, prices } = useChartData({
    url,
    name,
    min,
    max,
  });

  // We only want to show non-0-values
  const getFirstValidIndex = () => {
    if (!data?.datasets?.[0]?.data) return -1;

    const firstDataset = data.datasets[0].data;

    return firstDataset.findIndex((val: number | Point | null) => {
      if (val === null || val === undefined) return false;

      if (typeof val === "object" && "y" in val) {
        return (val.y as number) > 0;
      }

      return (val as number) > 0;
    });
  };

  const firstValidIndex = getFirstValidIndex();

  const filteredData =
    showChart && firstValidIndex > 0
      ? {
          ...data,
          labels: data.labels?.slice(firstValidIndex),
          datasets: data.datasets.map((dataset) => ({
            ...dataset,
            data: dataset.data.slice(firstValidIndex),
          })),
        }
      : data;

  const chartOptions: ChartOptions<"line"> = options
    ? { ...options }
    : {
        ...baseOptions,
        scales: {
          ...baseOptions.scales,
          x: {
            ...baseOptions.scales?.x,
            display: false,
            bounds: "data",
          },
          y: {
            ...baseOptions.scales?.y,
            display: true,
            border: {
              display: false,
            },
            grid: {
              display: true,
              color: "rgba(0, 0, 0, 0.1)",
            },
            ticks: {
              display: false,
            },
          },
        },
      };

  return (
    <div className={`is-full is-centered-row ${className}`}>
      {showChart && prices.length > 0 && (
        <Line
          ref={chartRef}
          data={filteredData}
          options={{ ...chartOptions }}
          height={"100%"}
        />
      )}
    </div>
  );
};
